/**
 * MiniCalendar Component
 * Enhanced mini calendar for date selection
 */

import React, { useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { monthNames } from '@/constants/calendar';
import { useHolidayMap } from '@/hooks/useHolidays';
import { useTenant } from '@/shared/contexts/TenantContext';

interface MiniCalendarProps {
  selectedDate: Date;
  onDateSelect: (date: Date) => void;
  onClose: () => void;
  /** Disable past dates (default: false) */
  disablePast?: boolean;
}

export const MiniCalendar: React.FC<MiniCalendarProps> = ({ selectedDate, onDateSelect, onClose, disablePast = false }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1));
  const { selectedProject } = useTenant();
  const holidayMap = useHolidayMap(currentMonth.getFullYear(), selectedProject?.country);

  // Get today's date at midnight for comparison
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const isPast = (date: Date | null) => {
    if (!date || !disablePast) return false;
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    return checkDate < today;
  };

  const daysOfWeek = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days: (Date | null)[] = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      if (direction === 'prev') {
        newMonth.setMonth(prev.getMonth() - 1);
      } else {
        newMonth.setMonth(prev.getMonth() + 1);
      }
      return newMonth;
    });
  };

  const isToday = (date: Date | null) => {
    if (!date) return false;
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isSelected = (date: Date | null) => {
    if (!date) return false;
    return date.toDateString() === selectedDate.toDateString();
  };

  const getHolidayName = (date: Date | null): string | null => {
    if (!date) return null;
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return holidayMap.get(`${date.getFullYear()}-${m}-${d}`) || null;
  };

  const days = getDaysInMonth(currentMonth);

  return (
    <div
      className="absolute z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl p-4 mt-1 w-72"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => navigateMonth('prev')}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
        >
          <ChevronLeftIcon className="h-4 w-4 text-gray-600 dark:text-gray-400" />
        </button>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </h3>
        <button
          onClick={() => navigateMonth('next')}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
        >
          <ChevronRightIcon className="h-4 w-4 text-gray-600 dark:text-gray-400" />
        </button>
      </div>

      {/* Days of week */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {daysOfWeek.map(day => (
          <div key={day} className="text-xs font-medium text-gray-500 dark:text-gray-400 text-center p-2">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar days */}
      <div className="grid grid-cols-7 gap-1 mb-4">
        {days.map((date, index) => {
          const holidayName = getHolidayName(date);
          return (
          <button
            key={index}
            onClick={() => {
              if (date && !isPast(date)) {
                // Mantener la hora actual
                const newDate = new Date(selectedDate);
                newDate.setFullYear(date.getFullYear());
                newDate.setMonth(date.getMonth());
                newDate.setDate(date.getDate());
                onDateSelect(newDate);
                onClose();
              }
            }}
            disabled={!date || isPast(date)}
            title={holidayName || undefined}
            className={`
              w-9 h-9 text-sm rounded-full flex items-center justify-center transition-all duration-200 font-medium relative
              ${!date ? 'invisible' : ''}
              ${isPast(date) ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed' : ''}
              ${isSelected(date) && !isPast(date) ? 'bg-sky-600 text-white shadow-lg scale-105' : ''}
              ${isToday(date) && !isSelected(date) ? 'bg-sky-100 dark:bg-sky-900 text-sky-600 dark:text-sky-400 font-bold ring-1 ring-sky-300 dark:ring-sky-700' : ''}
              ${holidayName && !isSelected(date) && !isToday(date) && !isPast(date) ? 'text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/20 font-semibold hover:bg-blue-100 dark:hover:bg-blue-950/40' : ''}
              ${date && !isSelected(date) && !isToday(date) && !isPast(date) && !holidayName ? 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 hover:scale-105' : ''}
            `}
          >
            {date?.getDate()}
            {holidayName && !isSelected(date) && (
              <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-blue-500" />
            )}
          </button>
          );
        })}
      </div>

      {/* Quick date options */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => {
            const today = new Date();
            const newDate = new Date(selectedDate);
            newDate.setFullYear(today.getFullYear());
            newDate.setMonth(today.getMonth());
            newDate.setDate(today.getDate());
            onDateSelect(newDate);
            onClose();
          }}
          className="flex-1 px-3 py-1.5 text-xs font-medium text-sky-600 dark:text-sky-400 bg-sky-50 dark:bg-sky-900/30 hover:bg-sky-100 dark:hover:bg-sky-900/50 rounded-md transition-colors"
        >
          Hoy
        </button>
        <button
          onClick={() => {
            const tomorrow = new Date();
            tomorrow.setDate(tomorrow.getDate() + 1);
            const newDate = new Date(selectedDate);
            newDate.setFullYear(tomorrow.getFullYear());
            newDate.setMonth(tomorrow.getMonth());
            newDate.setDate(tomorrow.getDate());
            onDateSelect(newDate);
            onClose();
          }}
          className="flex-1 px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-md transition-colors"
        >
          Mañana
        </button>
      </div>

      {/* Footer with close button */}
      <div className="flex justify-end pt-3 border-t border-gray-200 dark:border-gray-700">
        <button
          onClick={onClose}
          className="px-3 py-1.5 text-xs text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 font-medium"
        >
          Cerrar
        </button>
      </div>
    </div>
  );
};
